import React, { FC, PropsWithChildren } from 'react'
import { styled, css } from '@moonpig/launchpad-utils'
import { Box, Image, Link, Text } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import type { GuidedNBATile as GuidedNBATileType } from './types'

const TILE_SIZE_XS = 156
const TILE_WIDTH_SIZE_MD = 156
const TILE_HEIGHT_SIZE_MD = 156
const TILE_MIN_HEIGHT = 88
const TILE_MIN_WIDTH = 63
const MIN_TITLE_HEIGHT = 40

const StyledLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;

  ${s({
    width: { xs: TILE_SIZE_XS, md: TILE_WIDTH_SIZE_MD },
    height: { xs: TILE_SIZE_XS, md: TILE_HEIGHT_SIZE_MD },
    bgcolor: 'colorBackground01',
    p: { xs: 2, md: 4 },
    border: '2px solid',
    borderColor: 'transparent',
    borderRadius: 3,
    my: 4,
  })}

  &:hover,
  &:focus {
    text-decoration: none;
    ${s({
      borderColor: 'colorInteractionButtonHover',
    })}

    span {
      ${s({ color: 'colorInteractionButtonHover' })}
    }
  }
    
  &:focus-visible {
    outline: none;
  }
`

const StyledMedia = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  flex-shrink: 0;
  max-height: calc(100% - 44px);
  min-width: ${TILE_MIN_WIDTH}px;
  min-height: ${TILE_MIN_HEIGHT}px;

  /* Vertically balances the layout inside the card container */
  margin-top: auto;
  margin-bottom: auto;
`

const StyledImage = styled(Image)<{ $isCardImage: boolean }>`
  display: block;
  max-block-size: 100%;
  object-fit: contain;

  ${({ $isCardImage }) =>
    $isCardImage &&
    css`
      ${s({
        border: 1,
        borderColor: 'colorBorder03',
      })}
    `}
`

const StyledText = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
  min-height: ${MIN_TITLE_HEIGHT}px;

  ${s({
    textAlign: 'center',
    fontWeight: 400,
    typography: 'typeBodyText',
    color: 'colorTextHeadline',
    lineHeight: '1.25rem',
    pt: 4,
  })}

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: center;

    /* stylelint-disable value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    /* stylelint-enable */
  }
`

type Props = PropsWithChildren<
  GuidedNBATileType & {
    onClick: () => void
    isCardImage: boolean
  }
>

export const GuidedNBATile: FC<Props> = ({
  image,
  title,
  link,
  onClick,
  isCardImage,
}) => (
  <StyledLink href={link} onClick={onClick}>
    <StyledMedia>
      <StyledImage src={image} alt="" aria-hidden $isCardImage={isCardImage} />
    </StyledMedia>
    <StyledText>
      <span>{title}</span>
    </StyledText>
  </StyledLink>
)
